import clsx from 'clsx';
import React from 'react';
import { PageHeader } from './PageHeader';
import { Surface } from './Surface';

/**
 * Canvas page: the title sits on the field, and `children` are the boxed
 * regions underneath. `fill` adds the page gutter for routes that occupy
 * `main`. Legacy column pages already have that gutter, so they leave it off.
 */
export const Page: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  fill?: boolean;
  children?: React.ReactNode;
  className?: string;
}> = ({ title, description, meta, actions, fill, children, className }) => (
  <div
    className={clsx(
      'av2 flex min-w-0 flex-col gap-5',
      fill && 'min-h-0 flex-1 p-4 md:p-6 xl:px-8',
      className,
    )}
  >
    <PageHeader
      title={title}
      description={description}
      meta={meta}
      actions={actions}
    />
    {children}
  </div>
);

/**
 * The body of a list page: headline figures (`SummaryFacts`) standing on
 * their own, then one card holding the toolbar and the table. Use it
 * directly when a page shell is shared between several lists; otherwise
 * use `ListPage`.
 */
export const ListBody: React.FC<{
  summary?: React.ReactNode;
  toolbar?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ summary, toolbar, children }) => (
  <>
    {summary}
    <Surface>
      {toolbar ? (
        <div className="border-b border-v2-line">{toolbar}</div>
      ) : null}
      {children}
    </Surface>
  </>
);

/**
 * A list page: the title on the canvas, the figures under it, and one card
 * holding the toolbar and the table. The card has no title of its own.
 */
export const ListPage: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  summary?: React.ReactNode;
  toolbar?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}> = ({ summary, toolbar, children, className, ...header }) => (
  <Page {...header} className={className}>
    <ListBody summary={summary} toolbar={toolbar}>
      {children}
    </ListBody>
  </Page>
);

/**
 * A full-height workspace inside `FillPage`: the title on the canvas, and
 * one card filling the rest. `FillPage` already supplies the gutter.
 */
export const Workspace: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  /** Headline figures on their own card, between the title and the workspace. */
  summary?: React.ReactNode;
  toolbar?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}> = ({ summary, toolbar, children, className, ...header }) => (
  <div className={clsx('flex min-h-0 flex-1 flex-col gap-5', className)}>
    <PageHeader {...header} />
    {summary}
    <Surface className="flex min-h-0 flex-1 flex-col">
      {toolbar ? (
        <div className="border-b border-v2-line">{toolbar}</div>
      ) : null}
      {children}
    </Surface>
  </div>
);
